import { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, Tag, X, Check, Search, Store, Package, Sparkles } from "lucide-react";
import {
  api, formatApiErrorDetail, formatMoney,
  OFFER_TYPES, OFFER_UNITS, offerText, offerTypeLabel, offerValidityText,
} from "@/lib/api";
import AdminBrandOffers from "@/components/admin/AdminBrandOffers";

const EMPTY_OFFER = {
  offer_type: "price",
  offer_price: "",
  offer_description: "",
  offer_buy_qty: "",
  offer_buy_unit: "Case",
  offer_free_qty: "",
  offer_free_unit: "Piece",
  offer_free_item: "",
  offer_cash_discount: "",
  offer_start_date: "",
  offer_end_date: "",
};

export default function AdminOffers() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_OFFER);
  const [error, setError] = useState("");
  const [brand, setBrand] = useState("all");
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("product");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/products").then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (p) => {
    setError("");
    setEditing(p.id);
    setForm({
      offer_type: p.offer_type || "price",
      offer_price: p.offer_price ?? "",
      offer_description: p.offer_description ?? "",
      offer_buy_qty: p.offer_buy_qty ?? "",
      offer_buy_unit: p.offer_buy_unit || "Case",
      offer_free_qty: p.offer_free_qty ?? "",
      offer_free_unit: p.offer_free_unit || "Piece",
      offer_free_item: p.offer_free_item ?? "",
      offer_cash_discount: p.offer_cash_discount ?? "",
      offer_start_date: p.offer_start_date ?? "",
      offer_end_date: p.offer_end_date ?? "",
    });
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveOffer = async (p, enable) => {
    setError("");
    // Preserve all existing product fields; only touch offer fields.
    const base = { ...p };
    delete base.id;
    delete base.has_offer;
    const isFree = form.offer_type === "free_item" || form.offer_type === "quantity";
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

    let payload;
    if (!enable) {
      payload = {
        ...base,
        offer_price: null, offer_description: null, offer_type: null,
        offer_buy_qty: null, offer_buy_unit: null,
        offer_free_qty: null, offer_free_unit: null, offer_free_item: null,
        offer_cash_discount: null,
        offer_start_date: null, offer_end_date: null,
      };
    } else {
      payload = {
        ...base,
        offer_type: form.offer_type || null,
        offer_price: num(form.offer_price),
        offer_description: form.offer_description || null,
        offer_buy_qty: isFree ? num(form.offer_buy_qty) : null,
        offer_buy_unit: isFree ? (form.offer_buy_unit || null) : null,
        offer_free_qty: isFree ? num(form.offer_free_qty) : null,
        offer_free_unit: isFree ? (form.offer_free_unit || null) : null,
        offer_free_item: isFree ? (form.offer_free_item || null) : null,
        offer_cash_discount: isFree ? num(form.offer_cash_discount) : null,
        offer_start_date: form.offer_start_date || null,
        offer_end_date: form.offer_end_date || null,
      };
    }
    try {
      await api.put(`/products/${p.id}`, payload);
      setEditing(null);
      setForm(EMPTY_OFFER);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const activeOffers = products.filter((p) => p.has_offer);

  const brandOptions = useMemo(
    () => Array.from(new Set(products.map((p) => p.brand).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [products]
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const matchBrand = brand === "all" || p.brand === brand;
      const matchQuery = !q || p.name.toLowerCase().includes(q);
      return matchBrand && matchQuery;
    });
  }, [products, brand, query]);

  const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
  const labelCls = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500";
  const isFree = form.offer_type === "free_item" || form.offer_type === "quantity";

  return (
    <div data-testid="admin-offers">
      {/* Mode toggle: Product Offer vs Brand Offer */}
      <div className="mb-4 inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="offer-mode-toggle">
        <button
          data-testid="mode-product-offer"
          onClick={() => setMode("product")}
          className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition ${mode === "product" ? "bg-blue-600 text-white" : "text-slate-500 hover:text-blue-600"}`}
        >
          <Package className="h-4 w-4" /> Product Offer
        </button>
        <button
          data-testid="mode-brand-offer"
          onClick={() => setMode("brand")}
          className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition ${mode === "brand" ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-indigo-600"}`}
        >
          <Sparkles className="h-4 w-4" /> Brand Offer
        </button>
      </div>

      {mode === "brand" ? (
        <AdminBrandOffers />
      ) : (
      <>
      <p className="mb-4 text-sm text-slate-500">
        Turn offers on or off for any product. {activeOffers.length} active offer(s).
      </p>

      {/* Brand filter + product search */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative w-full sm:w-56">
          <Store className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <select
            data-testid="offers-brand-filter"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="w-full appearance-none rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="all">All Brands</option>
            {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="relative w-full sm:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="offers-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products..."
            className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : products.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400">Add products first to manage offers.</p>
      ) : shown.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400">No products match your filter.</p>
      ) : (
        <div className="space-y-2">
          {shown.map((p) => {
            const label = offerText(p);
            return (
              <div key={p.id} data-testid={`admin-offer-${p.id}`} className="rounded-lg border border-slate-100 bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{p.name}</p>
                    <p className="text-xs text-slate-400">
                      {p.brand} · {formatMoney(p.price) || "No price"}
                    </p>
                    {p.has_offer && (
                      <>
                        <p className="mt-1 inline-flex flex-wrap items-center gap-1 text-xs font-semibold text-amber-600" data-testid={`offer-tag-${p.id}`}>
                          <Tag className="h-3 w-3" />
                          {offerTypeLabel(p.offer_type) ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase">{offerTypeLabel(p.offer_type)}</span> : null}
                          {p.offer_price != null ? <span>{formatMoney(p.offer_price)}</span> : null}
                          {label ? <span className="font-medium text-amber-700">{label}</span> : null}
                        </p>
                        {offerValidityText(p) && (
                          <p className="mt-0.5 text-[11px] text-slate-500" data-testid={`offer-validity-${p.id}`}>
                            {offerValidityText(p)}
                            {" · "}
                            <span className={p.offer_active ? "font-semibold text-green-600" : "font-semibold text-slate-400"}>
                              {p.offer_active ? "Live now" : "Not active (scheduled/expired)"}
                            </span>
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`add-offer-${p.id}`}
                      onClick={() => openForm(p)}
                      className="rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                    >
                      {p.has_offer ? "Edit offer" : "Add offer"}
                    </button>
                    {p.has_offer && (
                      <button
                        data-testid={`remove-offer-${p.id}`}
                        onClick={() => saveOffer(p, false)}
                        className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {editing === p.id && (
                  <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>Offer Type</label>
                        <select
                          data-testid={`offer-type-${p.id}`}
                          value={form.offer_type}
                          onChange={(e) => set("offer_type", e.target.value)}
                          className={inputCls}
                        >
                          {OFFER_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Offer Price (₹) — optional</label>
                        <input
                          data-testid={`offer-price-${p.id}`}
                          type="number" step="0.01" inputMode="decimal"
                          value={form.offer_price}
                          onChange={(e) => set("offer_price", e.target.value)}
                          placeholder="Leave blank to keep selling price"
                          className={inputCls}
                        />
                      </div>
                    </div>

                    {isFree && (
                      <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3" data-testid={`offer-free-fields-${p.id}`}>
                        <p className="mb-2 text-xs font-semibold text-amber-700">Buy → Get Free details</p>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                          <div>
                            <label className={labelCls}>Buy Qty</label>
                            <input data-testid={`offer-buy-qty-${p.id}`} type="number" inputMode="numeric" value={form.offer_buy_qty} onChange={(e) => set("offer_buy_qty", e.target.value)} placeholder="10" className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls}>Buy Unit</label>
                            <select data-testid={`offer-buy-unit-${p.id}`} value={form.offer_buy_unit} onChange={(e) => set("offer_buy_unit", e.target.value)} className={inputCls}>
                              {OFFER_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className={labelCls}>Free Qty</label>
                            <input data-testid={`offer-free-qty-${p.id}`} type="number" inputMode="numeric" value={form.offer_free_qty} onChange={(e) => set("offer_free_qty", e.target.value)} placeholder="1" className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls}>Free Unit</label>
                            <select data-testid={`offer-free-unit-${p.id}`} value={form.offer_free_unit} onChange={(e) => set("offer_free_unit", e.target.value)} className={inputCls}>
                              {OFFER_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </div>
                          <div className="col-span-2 sm:col-span-1">
                            <label className={labelCls}>Free Item Name</label>
                            <input data-testid={`offer-free-item-${p.id}`} value={form.offer_free_item} onChange={(e) => set("offer_free_item", e.target.value)} placeholder="Agarbathi / Ceiling Fan" className={inputCls} />
                          </div>
                          <div className="col-span-2 sm:col-span-1">
                            <label className={labelCls}>Cash Discount (₹) — optional</label>
                            <input data-testid={`offer-cash-${p.id}`} type="number" step="0.01" inputMode="decimal" value={form.offer_cash_discount} onChange={(e) => set("offer_cash_discount", e.target.value)} placeholder="10" className={inputCls} />
                          </div>
                        </div>
                        {(form.offer_buy_qty || form.offer_free_qty) && (
                          <p className="mt-2 text-xs font-medium text-amber-700">
                            Preview: Buy {form.offer_buy_qty || "?"} {form.offer_buy_unit} → Get {form.offer_free_qty || "?"} {form.offer_free_unit} {form.offer_free_item || "item"} Free
                          </p>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>Offer Start Date</label>
                        <input
                          data-testid={`offer-start-date-${p.id}`}
                          type="date"
                          value={form.offer_start_date}
                          onChange={(e) => set("offer_start_date", e.target.value)}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Offer End Date</label>
                        <input
                          data-testid={`offer-end-date-${p.id}`}
                          type="date"
                          value={form.offer_end_date}
                          onChange={(e) => set("offer_end_date", e.target.value)}
                          className={inputCls}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Offer Description</label>
                      <input
                        data-testid={`offer-desc-${p.id}`}
                        value={form.offer_description}
                        onChange={(e) => set("offer_description", e.target.value)}
                        placeholder="e.g. ₹10 Discount, Festive Special"
                        className={inputCls}
                      />
                    </div>

                    <div className="flex gap-2">
                      <button data-testid={`save-offer-${p.id}`} onClick={() => saveOffer(p, true)} className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700">
                        <Check className="h-4 w-4" /> Save Offer
                      </button>
                      <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50">
                        <X className="h-4 w-4" /> Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
}
